import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { NewsService } from '@/services';
import { PostNewsCalendarNewsRequest, PutNewsCalendarNewsRequest } from '@/services/request/news';
import { NewsConverter } from '@/converters';

export const useNews = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_NEWS', params],
    async () => {
      const [err, data] = await NewsService.getNews(params);
      if (err) {
        throw err;
      }
      return {
        data: NewsConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useNewsById = (id: string) => {
  const query = useQuery(
    ['GET_NEWS_BY_ID', id],
    async () => {
      const [err, data] = await NewsService.getNewsById(id);
      if (err) {
        throw err;
      }
      return NewsConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostNewsMutation = () => {
  return useMutation({
    mutationKey: ['POST_NEWS'],
    mutationFn: async (payload: PostNewsCalendarNewsRequest) => {
      const [err, data] = await NewsService.postNews(payload);
      if (err) {
        throw err;
      }
      return NewsConverter.toDomainFromPostResponse(data);
    },
  });
};

type UsePutNewsMutationParams = {
  id: string;
  payload: PutNewsCalendarNewsRequest;
};

export const usePutNewsMutation = () => {
  return useMutation({
    mutationKey: ['PUT_NEWS'],
    mutationFn: async ({ id, payload }: UsePutNewsMutationParams) => {
      const [err, data] = await NewsService.putNews(id, payload);
      if (err) {
        throw err;
      }
      return NewsConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteNewsMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_NEWS'],
    mutationFn: async (id: string) => {
      const [err] = await NewsService.deleteNews(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetNewsByUUID = () => {
  return useMutation({
    mutationFn: async (uuid: string) => {
      const [err, data] = await NewsService.getNewsById(uuid);
      if (err) {
        throw err;
      }
      return NewsConverter.toDomainFromByIdResponse(data);
    },
  });
};
